import { PropsWithChildren } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { SentFaxUiStatus } from "@/Common/Models/UI/SentFaxUiStatus";
import { ResendFaxButton } from "@/Patient/Components/MedicalNote/SentFaxStatus/ResendFaxButton";
import { SentFaxInfoBox } from "@/Patient/Components/MedicalNote/SentFaxStatus/SentFaxInfoBox";
import { SentFaxStatusHelper } from "@/Patient/Helpers/SentFaxStatusHelper";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";

import "./FormSideWidgetContainer.scss";

class FormContainerButtonProps {
    public show?: boolean;
    public disabled?: boolean;
    public onClick?: () => void;
}

class FormContainerProps {
    public containerCssClass?: string;
    public patient: Patient;
    public containerId: string;
    public isExtended: boolean;
    public header: React.ReactNode;
    public onToggle: () => void;
    public delete?: FormContainerButtonProps;
    public sentFaxes?: SentFaxInfo[];
    public fax?: FormContainerButtonProps;
    public print?: FormContainerButtonProps;
    public isSecondScreen?: boolean;
    public portal?: FormContainerButtonProps & { item?: IPortalShareableItem };
    public contributionId?: string;
}

class FormContainer extends BaseReactComponent<PropsWithChildren<FormContainerProps>, null> {
    private readonly _btnClassName: string = "header-btn";

    public render(): JSX.Element {
        const formHeaderBorderClass = this.props.isExtended ? "border-bottom-thin" : "";
        const showSentFaxes = cacheStore.ClinicSettings.EnableFaxSendingIndicator && this.props.sentFaxes && !this.props.isSecondScreen;
        const showGeneralResendButton = showSentFaxes && this.props.sentFaxes.any(fax => SentFaxStatusHelper.convertToUiStatus(fax.SentStatus) === SentFaxUiStatus.Failed);

        return (
            <div className={"medical-notes-forms " + (this.props.containerCssClass ?? "")} id={this.props.containerId}>
                <div className={`flex-container form-header obstetrical-header-wrapper ${formHeaderBorderClass}`} onClick={e => this.onHeaderClick(e)}>
                    <div className="nav-page-container">
                        {this.props.header}
                    </div>
                    <div className="obstetrical-header-wrapper">
                        {this.renderRightSection()}
                        {showSentFaxes && this.renderSentFaxButton()}
                        {showGeneralResendButton && (
                            <div className="text-bold marginleft10">
                                <ResendFaxButton
                                    sentFaxItemId={this.props.sentFaxes.firstOrDefault()?.SentFaxItemId}
                                    sentFaxIds={[SentFaxStatusHelper.getLastFailedSentFaxId(this.props.sentFaxes)]}
                                />
                            </div>
                        )}
                    </div>
                    <div className="obstetrical-header-wrapper">
                        {this.renderPortalButtonAndChevron()}
                    </div>
                </div>
                {this.props.isExtended && this.props.children}
            </div>
        );
    }

    private renderRightSection(): JSX.Element {
        return (
            <div className="right-section form-action-btn flex-container align-center">
                {this.renderButton("DeleteLabel", this.props.delete, "color-red")}
                {this.renderButton("PrintLabel", this.props.print, "text-bold")}
                {this.renderButton("FaxVerbLabel", this.props.fax, "text-bold")}
            </div>
        );
    }

    private renderPortalButtonAndChevron(): JSX.Element {
        const chevronCssClass = this.props.isExtended ? "glyphicon-chevron-up" : "glyphicon-chevron-down";

        return (
            <div className="right-section form-action-btn flex-container align-center">
                {this.renderPortalButton()}
                <span
                    className={`flex-container align-center glyphicon ${chevronCssClass} marginleft10`}
                    onClick={event => { event.stopPropagation(); this.props.onToggle(); }}
                />
            </div>
        );
    }

    private renderPortalButton(): JSX.Element {
        return this.props.portal?.show && (
            <div className={`marginleft10 ${this._btnClassName}`}>
                <PortalStatusButton
                    disabled={this.props.portal?.disabled}
                    onClick={e => {
                        e.stopPropagation();
                        this.props.portal?.onClick?.();
                    }}
                    {...PortalHelper.getPortalButtonPropsForShareableItem(this.props.portal.item)}
                />
            </div>
        );
    }

    private renderButton(labelKey: string, buttonProps: FormContainerButtonProps, className: string = ""): JSX.Element {
        if (!buttonProps?.show)
            return null;

        return (
            <button
                className={`btn-link ${this._btnClassName} ${className}`}
                disabled={buttonProps?.disabled}
                onClick={() => buttonProps?.onClick?.()}
            >
                {cacheStore.GetString(labelKey)}
            </button>
        );
    }

    private renderSentFaxButton(): JSX.Element {
        return (
            <SentFaxInfoBox
                data={this.props.sentFaxes.map(fax => ({
                    sentFaxItemId: fax.SentFaxItemId,
                    sentBy: fax.SenderEmployeeName,
                    sentDate: fax.SentDate,
                    faxStatus: fax.SentStatus,
                    recipient: fax.RecipientName,
                    recipientFax: fax.RecipientFax,
                    receivedDate: fax.ReceiveDate,
                    faxId: fax.Id,
                    itemType: fax.SentFaxItemTypeId,
                    senderEmployeeId: fax.SenderEmployeeId
                }))}
                parentElementId="medical-note-container"
                tooltipId={`react-tooltip-${this.props.contributionId}`}
                iconBtnWrapperClassName={this._btnClassName}
            />
        );
    }

    private onHeaderClick(e: React.MouseEvent): void {
        if ($(e.target).closest(this._btnClassName).length > 0)
            return; // ignore button clicks which are handled elsewhere

        this.props.onToggle?.();
    }
}

export { FormContainer };
